/**
 * Claim Service - Handles API interactions for claims management
 */

import axios from 'axios';
import { get, post, put, del } from '../utils/apiUtils';

// No need to use BASE_URL as it's already configured in apiUtils.js
const API_URL =  `https://product-dev-bluemormon.metamorphosys.co.in/auto-adjudication/api/v1`
// const API_URL = `https://no-touch-adjudication.metamorphosys.co.in/api/v1/`;
// https://no-touch-adjudication.metamorphosys.co.in/api/genai/getlastrecords%27



/**
 * Get all claims with criteria
 * @param {any} searchCriteria - Search criteria
 * @returns {Promise<Object>} Promise resolving to claims listing
 */
export const getClaimsByCriteria = async (searchCriteria) => {
  try {
    const response = await get(`${API_URL}/getlatest`);
    return response || []; // Ensure we always return an array
  } catch (error) {
    console.error('Error fetching Claims:', error);
    throw error;
  }
};
/**
 * Get a specific claim by ID
 * @param {string} claimId - Claim ID
 * @returns {Promise<Object>} Promise resolving to claim object
 */
export const getClaimById = async (claimId) => {
  try {
    const response = await get(`${API_URL}/getClaimByGuid/${claimId}`);
    return transformClaimData(response);
  } catch (error) {
    console.error(`Error fetching claim ${claimId}:`, error);
    throw error;
  }
};

/**
 * Create a new claim
 * @param {Object} claimData - Claim data
 * @returns {Promise<Object>} Promise resolving to created claim
 */
export const createClaim = async (claimData) => {
  try {
    const response = await post(API_URL, claimData);
    return response.data;
  } catch (error) {
    console.error('Error creating claim:', error);
    throw error;
  }
};

/**
 * Update an existing claim
 * @param {string} claimId - Claim ID
 * @param {Object} claimData - Updated claim data
 * @returns {Promise<Object>} Promise resolving to updated claim
 */
export const updateClaim = async (claimId, claimData) => {
  try {
    const response = await put(`${API_URL}/${claimId}`, claimData);
    return response.data;
  } catch (error) {
    console.error(`Error updating claim ${claimId}:`, error);
    throw error;
  }
};

/**
 * Delete a claim
 * @param {string} claimId - Claim ID
 * @returns {Promise<void>} Promise resolving when claim is deleted
 */
export const deleteClaim = async (claimId) => {
  try {
    await del(`${API_URL}/${claimId}`);
  } catch (error) {
    console.error(`Error deleting claim ${claimId}:`, error);
    throw error;
  }
};

/**
 * Get claim timeline events
 * @param {string} claimId - Claim ID
 * @returns {Promise<Array>} Promise resolving to array of timeline events
 */
export const getClaimTimeline = async (claimId) => {
  try {
    const response = await get(`${API_URL}/${claimId}/timeline`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching timeline for claim ${claimId}:`, error);
    throw error;
  }
};

/**
 * Add a timeline event to a claim
 * @param {string} claimId - Claim ID
 * @param {Object} eventData - Timeline event data
 * @returns {Promise<Object>} Promise resolving to created event
 */
export const addClaimTimelineEvent = async (claimId, eventData) => {
  try {
    const response = await post(`${API_URL}/${claimId}/timeline`, eventData);
    return response.data;
  } catch (error) {
    console.error(`Error adding timeline event to claim ${claimId}:`, error);
    throw error;
  }
};

/**
 * Get documents associated with a claim
 * @param {string} claimId - Claim ID
 * @returns {Promise<Array>} Promise resolving to array of documents
 */
export const getClaimDocuments = async (claimId) => {
  try {
    const response = await get(`${API_URL}/${claimId}/documents`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching documents for claim ${claimId}:`, error);
    throw error;
  }
};

/**
 * Upload a document to a claim
 * @param {string} claimId - Claim ID
 * @param {FormData} formData - Form data containing the document
 * @returns {Promise<Object>} Promise resolving to uploaded document
 */
export const uploadClaimDocument = async (claimId, formData) => {
  try {
    const response = await post(`${API_URL}/${claimId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Error uploading document to claim ${claimId}:`, error);
    throw error;
  }
};

/**
 * Process a claim with AI
 * @param {string} claimId - Claim ID
 * @param {Object} processingOptions - Processing options
 * @returns {Promise<Object>} Promise resolving to processing results
 */
export const processClaimWithAI = async (claimId, processingOptions = {}) => {
  try {
    const response = await post(`${API_URL}/${claimId}/process`, processingOptions);
    return response.data;
  } catch (error) {
    console.error(`Error processing claim ${claimId} with AI:`, error);
    throw error;
  }
};

/**
 * Assign a claim to a user
 * @param {string} claimId - Claim ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Promise resolving to updated claim
 */
export const assignClaim = async (claimId, userId) => {
  try {
    const response = await post(`${API_URL}/${claimId}/assign`, { userId });
    return response.data;
  } catch (error) {
    console.error(`Error assigning claim ${claimId} to user ${userId}:`, error);
    throw error;
  }
};

/**
 * Update claim status
 * @param {string} claimId - Claim ID
 * @param {string} status - New status
 * @param {string} [notes] - Optional notes about the status change
 * @returns {Promise<Object>} Promise resolving to updated claim
 */
export const updateClaimStatus = async (claimId, status, notes) => {
  try {
    const response = await post(`${API_URL}/${claimId}/status`, { status, notes });
    return response.data;
  } catch (error) {
    console.error(`Error updating status for claim ${claimId}:`, error);
    throw error;
  }
};

/**
 * Transform API response to match the frontend data structure
 * @param {Object} apiResponse - The API response data
 * @returns {Object} Transformed data matching frontend structure
 */
const transformClaimData = (apiResponse) => {
  if (!apiResponse || !apiResponse.claim) {
    return null;
  }
  
  const { 
    claim, 
    coverageBenefits, 
    documents, 
    payouts = [], 
    agenticAdjudicationReport,
    agentActionsTimeline,
    decisionSummary,
    _id,
    transaction_id,
    extensions
  } = apiResponse;
  
  const { claimInformation, policyInformation, status, metadata } = claim;
  return {
    claim_id: claim.id,
    transactionId: transaction_id,
    mongoId: _id?.$oid,
    patient: claimInformation.patient.name,
    policyNumber: claimInformation.policyNumber,
    type: claimInformation.claimType,
    status: status.toLowerCase(),
    priority: getPriorityFromConfidence(claimInformation.aiConfidence),
    date: metadata.createdAt,
    submissionDate: claimInformation.submissionDate,
    confidence: claimInformation.aiConfidence / 100, // Convert from percentage to decimal
    amount: claimInformation.claimAmount,
    provider: claimInformation.provider.name,
    diagnosis: claimInformation.diagnosis,
    communication: extensions?.communicationHistory || [], // Map from extensions if available
    policyDetails: {
      holder: policyInformation.policyHolder,
      startDate: policyInformation.startDate,
      endDate: policyInformation.endDate,
      type: policyInformation.policyType,
      coverageLimit: getCoverageLimitFromBenefits(coverageBenefits),
      deductible: 500, // Default value as it's not in API response
      copay: 20 // Default value as it's not in API response
    },
    decisionSummary: decisionSummary,
    adjudicationReport: agenticAdjudicationReport,
    benefits: coverageBenefits.map(benefit => ({
      name: benefit.category,
      covered: benefit.status === 'Covered',
      limit: benefit.maxLimit,
      used: benefit.amountUsed
    })),
    payouts: payouts.map(payout => ({
      id: payout.payoutId || `PAY-${Math.random().toString(36).substr(2, 9)}`,
      date: payout.date || new Date().toISOString(),
      amount: payout.amount || 0,
      status: (payout.status || 'pending').toLowerCase(),
      method: payout.method || 'bank transfer'
    })),
    documents: documents.map(doc => ({
      id: generateDocId(doc.name),
      name: doc.name,
      type: doc.type || 'application/pdf',
      size: doc.size || 0,
      date: metadata.createdAt, // Using claim creation date as fallback
      status: 'verified',
      link: doc.file_id || doc.file_key || '', // Handle both file_id and file_key
      path: doc.path || ''
    })),
    agentMetrics: apiResponse.agentActionsTimeline?.metrics,
    agentActions: apiResponse.agentActionsTimeline?.actions || []
  };
};

// Helper functions
const getPriorityFromConfidence = (confidence) => {
  if (confidence >= 90) return 'low';
  if (confidence >= 75) return 'medium';
  return 'high';
};

const getCoverageLimitFromBenefits = (benefits) => {
  return benefits.reduce((total, benefit) => total + benefit.maxLimit, 0);
};

const generateDocId = (name) => {
  return `DOC-${name.slice(0, 3).toUpperCase()}`;
};

const getDetailsFromAction = (action) => {
  if (action.performance) {
    return `Processed with ${action.performance.accuracy}% accuracy in ${action.performance.processTime}. Risk Level: ${action.performance.riskLevel}`;
  }
  return action.details;
};

export default {
  getClaimsByCriteria,
  getClaimById,
  createClaim,
  updateClaim,
  deleteClaim,
  getClaimTimeline,
  addClaimTimelineEvent,
  getClaimDocuments,
  uploadClaimDocument,
  processClaimWithAI,
  assignClaim,
  updateClaimStatus
};

